import { useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  Bot,
  Briefcase,
  DollarSign,
  Eye,
  FileCheck,
  FileText,
  MessageSquare,
  Users,
  X,
  Radar,
  FolderKanban,
  Rocket,
  Phone,
} from "lucide-react";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { getStripeEnvironment } from "@/lib/stripe";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";

import {
  adminDeletePortfolioProject,
  adminListInquiries,
  adminListOrders,
  adminListPortfolioProjects,
  adminSendBalanceInvoice,
  adminUpdateInquiryStatus,
  adminUpdateProjectMilestone,
  adminUpsertPortfolioProject,
  type AdminBrief,
  type AdminOrder,
} from "@/utils/admin.functions";
import type { PortfolioProject } from "@/utils/projects.functions";
import {
  adminListProposals,
  adminCreateProposal,
  adminDeleteProposal,
  adminUpdateProposal,
  adminSendProposal,
  type ProjectProposal,
  DEFAULT_TERMS,
} from "@/utils/proposals.functions";

import { AdminProjectsView, type FilterTab } from "@/components/admin/AdminProjectsView";
import { AdminInquiriesView } from "@/components/admin/AdminInquiriesView";
import { AdminChatsView } from "@/components/admin/AdminChatsView";
import { adminListChats, adminUpdateChatStatus } from "@/utils/chats.functions";
import { AdminProposalsView } from "@/components/admin/AdminProposalsView";
import { AdminPortfolioCMS } from "@/components/admin/AdminPortfolioCMS";
import { AdminFinancialsView } from "@/components/admin/AdminFinancialsView";
import { AdminPipelineView } from "@/components/admin/AdminPipelineView";
import { AdminAutopilotView } from "@/components/admin/AdminAutopilotView";
import { AdminProspectsView } from "@/components/admin/AdminProspectsView";
import { AdminPortalView } from "@/components/admin/AdminPortalView";
import { AdminOnboardingView } from "@/components/admin/AdminOnboardingView";
import { AdminSignalView } from "@/components/admin/AdminSignalView";
import { AdminRedesignView } from "@/components/admin/AdminRedesignView";
import { AdminOpportunityView } from "@/components/admin/AdminOpportunityView";
import { AdminColdCallsView, type ColdCallPrefill } from "@/components/admin/AdminColdCallsView";
import {
  adminListColdCalls,
  adminStartColdCall,
  adminDeleteColdCall,
} from "@/utils/coldcalls.functions";
import {
  adminListRedesignRuns,
  adminRunRedesign,
  adminUpdateRedesignRun,
  adminDeleteRedesignRun,
  adminSaveRedesignOutreach,
  adminSendRedesignPitch,
  adminDownloadRedesignPdf,
  type RedesignAngle,
  type RedesignStatus,
  type RedesignTreatment,
} from "@/utils/redesign.functions";
import {
  adminListOnboarding,
  adminRunOnboarding,
  adminApproveOnboarding,
  adminRetryOnboarding,
  adminDismissOnboarding,
} from "@/utils/onboarding.functions";
import {
  adminListProspects,
  adminFindProspects,
  adminScanPending,
  adminDraftOutreach,
  adminSaveProspectDraft,
  adminSendOutreach,
  adminUpdateProspect,
  adminGenerateVariants,
  adminSelectVariant,
  adminSyncProspectCrm,
  adminProspectAnalytics,
} from "@/utils/prospects.functions";

import {
  adminListPipeline,
  adminUpdateLeadStage,
  adminUpdateBookingStatus,
  adminResolveFollowup,
} from "@/utils/crm.functions";
import {
  adminGetAutopilot,
  adminRunAutopilot,
  adminSetAutopilotStatus,
  adminApproveDraft,
  adminDismissDraft,
  adminRetryDraft,
  adminUpdateDraft,
} from "@/utils/automation.functions";

export const Route = createFileRoute("/_authenticated/admin")({
  head: () => ({
    meta: [
      { title: "Studio Command Hub — theroyeffect.com" },
      {
        name: "description",
        content:
          "Studio owner management hub: current projects, client leads, portfolio editor, and billing.",
      },
      { property: "og:title", content: "Studio Command Hub — theroyeffect.com" },
      { property: "og:description", content: "Studio owner dashboard." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: AdminPage,
});

const money = (cents: number, currency: string) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: currency.toUpperCase() }).format(
    cents / 100,
  );

const date = (value: string | null) =>
  value
    ? new Date(value).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : "—";

type MainView =
  | "SIGNAL"
  | "PROJECTS"
  | "PIPELINE"
  | "AUTOPILOT"
  | "SETUP"
  | "PROSPECTS"
  | "INQUIRIES"
  | "CHATS"
  | "PROPOSALS"
  | "PORTFOLIO"
  | "CLIENTPORTAL"
  | "REDESIGN"
  | "OPPORTUNITY"
  | "COLDCALLS"
  | "FINANCIALS";

function AdminPage() {
  const environment = getStripeEnvironment();
  const queryClient = useQueryClient();

  const listOrders = useServerFn(adminListOrders);
  const sendInvoice = useServerFn(adminSendBalanceInvoice);
  const updateMilestone = useServerFn(adminUpdateProjectMilestone);
  const listInquiries = useServerFn(adminListInquiries);
  const listChats = useServerFn(adminListChats);
  const updateChatStatus = useServerFn(adminUpdateChatStatus);
  const updateInquiry = useServerFn(adminUpdateInquiryStatus);
  const listPortfolio = useServerFn(adminListPortfolioProjects);
  const upsertPortfolio = useServerFn(adminUpsertPortfolioProject);
  const deletePortfolio = useServerFn(adminDeletePortfolioProject);
  const listProposals = useServerFn(adminListProposals);
  const createProposal = useServerFn(adminCreateProposal);
  const deleteProposal = useServerFn(adminDeleteProposal);
  const updateProposal = useServerFn(adminUpdateProposal);
  const sendProposal = useServerFn(adminSendProposal);
  const listPipeline = useServerFn(adminListPipeline);
  const updateLeadStage = useServerFn(adminUpdateLeadStage);
  const updateBookingStatus = useServerFn(adminUpdateBookingStatus);
  const resolveFollowup = useServerFn(adminResolveFollowup);
  const getAutopilot = useServerFn(adminGetAutopilot);
  const runAutopilot = useServerFn(adminRunAutopilot);
  const setAutopilotStatus = useServerFn(adminSetAutopilotStatus);
  const approveDraftFn = useServerFn(adminApproveDraft);
  const dismissDraftFn = useServerFn(adminDismissDraft);
  const retryDraftFn = useServerFn(adminRetryDraft);
  const updateDraftFn = useServerFn(adminUpdateDraft);
  const listProspects = useServerFn(adminListProspects);
  const findProspects = useServerFn(adminFindProspects);
  const scanPendingFn = useServerFn(adminScanPending);
  const draftOutreachFn = useServerFn(adminDraftOutreach);
  const saveProspectDraftFn = useServerFn(adminSaveProspectDraft);
  const sendOutreachFn = useServerFn(adminSendOutreach);
  const updateProspectFn = useServerFn(adminUpdateProspect);
  const generateVariantsFn = useServerFn(adminGenerateVariants);
  const selectVariantFn = useServerFn(adminSelectVariant);
  const syncProspectCrmFn = useServerFn(adminSyncProspectCrm);
  const prospectAnalyticsFn = useServerFn(adminProspectAnalytics);
  const listOnboarding = useServerFn(adminListOnboarding);
  const runOnboardingFn = useServerFn(adminRunOnboarding);
  const approveOnboardingFn = useServerFn(adminApproveOnboarding);
  const retryOnboardingFn = useServerFn(adminRetryOnboarding);
  const dismissOnboardingFn = useServerFn(adminDismissOnboarding);

  const [currentView, setCurrentView] = useState<MainView>("SIGNAL");
  const [filterTab, setFilterTab] = useState<FilterTab>("ALL");
  const [searchQuery, setSearchQuery] = useState("");
  const [busy, setBusy] = useState<string | null>(null);

  // Modals state
  const [selectedBrief, setSelectedBrief] = useState<AdminBrief | null>(null);
  const [editingProject, setEditingProject] = useState<PortfolioProject | null>(null);
  const [isNewProjectModalOpen, setIsNewProjectModalOpen] = useState(false);
  const [isProposalModalOpen, setIsProposalModalOpen] = useState(false);
  const [editingProposalId, setEditingProposalId] = useState<string | null>(null);

  // Proposal form state
  const [proposalForm, setProposalForm] = useState<{
    briefId?: string;
    clientName: string;
    clientEmail: string;
    clientCompany: string;
    projectTitle: string;
    scopeDeliverables: string;
    timelineWeeks: string;
    totalPriceDollars: number;
    terms: string;
  }>({
    clientName: "",
    clientEmail: "",
    clientCompany: "",
    projectTitle: "",
    scopeDeliverables:
      "• Visual Direction Sprint (Type, Color, UI Language)\n• Full Mobile & Desktop Screen Designs in Figma\n• Complete Production Build & Interactive Animations\n• Stripe eCommerce / Payment Integration\n• SEO Meta Architecture & Performance Optimization\n• 2 Comprehensive Revision Rounds",
    timelineWeeks: "2–3 Weeks",
    totalPriceDollars: 5000,
    terms: DEFAULT_TERMS,
  });

  // Form state for editing/adding projects
  const [projectForm, setProjectForm] = useState<{
    id?: string;
    title: string;
    tagline: string;
    description: string;
    url: string;
    category: "Brand Identity" | "UI/UX" | "No-Code";
    metric: string;
    tags: string;
    isPublished: boolean;
  }>({
    title: "",
    tagline: "",
    description: "",
    url: "",
    category: "UI/UX",
    metric: "",
    tags: "",
    isPublished: true,
  });

  // Queries
  const {
    data: ordersData,
    isLoading: ordersLoading,
    error: ordersError,
  } = useQuery({
    queryKey: ["admin-orders", environment],
    queryFn: () => listOrders({ data: { environment } }),
    retry: false,
  });

  const { data: inquiriesData } = useQuery({
    queryKey: ["admin-inquiries"],
    queryFn: () => listInquiries(),
    retry: false,
  });

  const { data: chatsData } = useQuery({
    queryKey: ["admin-chats"],
    queryFn: () => listChats(),
    retry: false,
    refetchInterval: 60_000,
  });

  const setChatStatus = async (conversationId: string, status: "new" | "handled") => {
    try {
      await updateChatStatus({ data: { conversationId, status } });
      await queryClient.invalidateQueries({ queryKey: ["admin-chats"] });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not update chat");
    }
  };

  const { data: proposalsData } = useQuery({
    queryKey: ["admin-proposals"],
    queryFn: () => listProposals(),
    retry: false,
  });

  const { data: pipelineData } = useQuery({
    queryKey: ["admin-pipeline"],
    queryFn: () => listPipeline(),
    retry: false,
  });

  const { data: autopilotData } = useQuery({
    queryKey: ["admin-autopilot"],
    queryFn: () => getAutopilot(),
    retry: false,
  });

  const refreshAutopilot = () => queryClient.invalidateQueries({ queryKey: ["admin-autopilot"] });

  const { data: onboardingData } = useQuery({
    queryKey: ["admin-onboarding"],
    queryFn: () => listOnboarding(),
    retry: false,
  });

  const refreshOnboarding = () => queryClient.invalidateQueries({ queryKey: ["admin-onboarding"] });

  const runOnboardingQueue = async () => {
    setBusy("onboarding-run");
    try {
      const result = await runOnboardingFn();
      toast.success(
        result.processed > 0
          ? `${result.processed} purchase(s) set up.`
          : "Nothing waiting to set up.",
      );
      await refreshOnboarding();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Setup run failed");
    } finally {
      setBusy(null);
    }
  };

  const approveOnboarding = async (id: string) => {
    setBusy(id);
    try {
      await approveOnboardingFn({ data: { id } });
      await refreshOnboarding();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not update");
    } finally {
      setBusy(null);
    }
  };

  const retryOnboarding = async (id: string) => {
    setBusy(id);
    try {
      const result = await retryOnboardingFn({ data: { id } });
      if (result.ok) toast.success("Setup re-run.");
      else toast.error(result.message ?? "Setup failed");
      await refreshOnboarding();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Setup failed");
    } finally {
      setBusy(null);
    }
  };

  const dismissOnboarding = async (id: string) => {
    setBusy(id);
    try {
      await dismissOnboardingFn({ data: { id } });
      await refreshOnboarding();
    } finally {
      setBusy(null);
    }
  };

  const runAutopilotScan = async () => {
    setBusy("autopilot-run");
    try {
      const result = await runAutopilot();
      if (result.status === "paused") toast.error(result.message ?? "Autopilot paused");
      else if (result.status === "skipped_locked") toast.info("A scan is already running.");
      else toast.success(`Scan complete — ${result.drafted} new draft(s).`);
      await refreshAutopilot();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Scan failed");
    } finally {
      setBusy(null);
    }
  };

  const toggleAutopilot = async (paused: boolean) => {
    setBusy("autopilot-toggle");
    try {
      await setAutopilotStatus({ data: { paused } });
      toast.success(paused ? "Autopilot paused" : "Autopilot resumed");
      await refreshAutopilot();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not update autopilot");
    } finally {
      setBusy(null);
    }
  };

  const approveDraft = async (id: string) => {
    setBusy(id);
    try {
      const result = await approveDraftFn({ data: { id } });
      if (result.ok) toast.success("Follow-up sent.");
      else toast.error(result.message ?? "Not sent");
      await refreshAutopilot();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Send failed");
      await refreshAutopilot();
    } finally {
      setBusy(null);
    }
  };

  const dismissDraft = async (id: string) => {
    setBusy(id);
    try {
      await dismissDraftFn({ data: { id } });
      await refreshAutopilot();
    } finally {
      setBusy(null);
    }
  };

  const retryDraft = async (id: string) => {
    setBusy(id);
    try {
      await retryDraftFn({ data: { id } });
      await refreshAutopilot();
    } finally {
      setBusy(null);
    }
  };

  const saveDraft = async (id: string, subject: string, body: string) => {
    setBusy(id);
    try {
      await updateDraftFn({ data: { id, subject, body } });
      toast.success("Draft updated.");
      await refreshAutopilot();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not save draft");
    } finally {
      setBusy(null);
    }
  };

  const listRedesignRuns = useServerFn(adminListRedesignRuns);
  const runRedesignFn = useServerFn(adminRunRedesign);
  const updateRedesignFn = useServerFn(adminUpdateRedesignRun);
  const deleteRedesignFn = useServerFn(adminDeleteRedesignRun);
  const saveRedesignOutreachFn = useServerFn(adminSaveRedesignOutreach);
  const sendRedesignPitchFn = useServerFn(adminSendRedesignPitch);
  const downloadRedesignPdfFn = useServerFn(adminDownloadRedesignPdf);

  const { data: redesignData } = useQuery({
    queryKey: ["admin-redesign"],
    queryFn: () => listRedesignRuns(),
    retry: false,
  });

  const refreshRedesign = () => queryClient.invalidateQueries({ queryKey: ["admin-redesign"] });

  const startRedesign = async (input: {
    url: string;
    treatment: RedesignTreatment;
    angle: RedesignAngle;
  }) => {
    setBusy("redesign-run");
    try {
      const { run } = await runRedesignFn({ data: input });
      toast.success(`Pitch ready for ${run.host}`);
      await refreshRedesign();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "That redesign run failed");
      await refreshRedesign();
    } finally {
      setBusy(null);
    }
  };

  const setRedesignStatus = async (id: string, status: RedesignStatus) => {
    try {
      await updateRedesignFn({ data: { id, status } });
      await refreshRedesign();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not update that run");
    }
  };

  const removeRedesignRun = async (id: string) => {
    try {
      await deleteRedesignFn({ data: { id } });
      toast.success("Run deleted");
      await refreshRedesign();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not delete that run");
    }
  };

  const saveRedesignOutreach = async (
    id: string,
    subject: string,
    body: string,
    contactEmail: string | null,
  ) => {
    setBusy(`redesign-save-${id}`);
    try {
      await saveRedesignOutreachFn({ data: { id, subject, body, contactEmail } });
      toast.success("Outreach saved.");
      await refreshRedesign();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not save that draft");
    } finally {
      setBusy(null);
    }
  };

  const sendRedesignPitch = async (id: string) => {
    setBusy(`redesign-send-${id}`);
    try {
      const result = await sendRedesignPitchFn({ data: { id } });
      if (result.ok) toast.success("Pitch sent.");
      else toast.error("Not delivered — that address is suppressed.");
      await refreshRedesign();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Send failed");
      await refreshRedesign();
    } finally {
      setBusy(null);
    }
  };

  const exportRedesignPdf = async (id: string) => {
    setBusy(`redesign-pdf-${id}`);
    try {
      const res = await downloadRedesignPdfFn({ data: { id } });
      if (!res.success) throw new Error(res.error || "Failed");
      const link = document.createElement("a");
      link.href = `data:application/pdf;base64,${res.pdfBase64}`;
      link.download = res.filename;
      link.click();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not export the PDF");
    } finally {
      setBusy(null);
    }
  };

  const listColdCalls = useServerFn(adminListColdCalls);
  const startColdCallFn = useServerFn(adminStartColdCall);
  const deleteColdCallFn = useServerFn(adminDeleteColdCall);
  const [coldCallPrefill, setColdCallPrefill] = useState<ColdCallPrefill | null>(null);

  const { data: coldCallData } = useQuery({
    queryKey: ["admin-cold-calls"],
    queryFn: () => listColdCalls(),
    retry: false,
    refetchInterval: 20000,
  });

  const refreshColdCalls = () => queryClient.invalidateQueries({ queryKey: ["admin-cold-calls"] });

  const startColdCall = async (input: {
    businessName: string;
    phone: string;
    website: string | null;
    talkingPoints: string | null;
    redesignRunId: string | null;
  }) => {
    setBusy("cold-call");
    try {
      await startColdCallFn({ data: input });
      toast.success(`Calling ${input.businessName} now.`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "That call could not be placed");
    } finally {
      setBusy(null);
      await refreshColdCalls();
    }
  };

  const removeColdCall = async (id: string) => {
    try {
      await deleteColdCallFn({ data: { id } });
      await refreshColdCalls();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not delete that call");
    }
  };

  const { data: prospectsData } = useQuery({
    queryKey: ["admin-prospects"],
    queryFn: () => listProspects(),
    retry: false,
  });

  const { data: prospectAnalytics } = useQuery({
    queryKey: ["admin-prospect-analytics"],
    queryFn: () => prospectAnalyticsFn(),
    retry: false,
  });

  const refreshProspects = async () => {
    await queryClient.invalidateQueries({ queryKey: ["admin-prospects"] });
    await queryClient.invalidateQueries({ queryKey: ["admin-prospect-analytics"] });
  };

  const generateProspectVariants = async (id: string) => {
    setBusy(`variants-${id}`);
    try {
      await generateVariantsFn({ data: { id } });
      toast.success("Two variants ready — pick one to send.");
      await refreshProspects();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not write the variants");
    } finally {
      setBusy(null);
    }
  };

  const chooseProspectVariant = async (id: string, key: "A" | "B") => {
    setBusy(`variant-${id}`);
    try {
      await selectVariantFn({ data: { id, key } });
      toast.success(`Variant ${key} applied to the draft.`);
      await refreshProspects();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not apply that variant");
    } finally {
      setBusy(null);
    }
  };

  const syncProspectPipeline = async () => {
    setBusy("sync-crm");
    try {
      const result = await syncProspectCrmFn();
      toast.success(`Synced — ${result.booked} call(s) and ${result.won} sale(s) matched.`);
      await refreshProspects();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Sync failed");
    } finally {
      setBusy(null);
    }
  };

  const findProspectsFor = async (industry: string) => {
    setBusy("find");
    try {
      const result = await findProspects({ data: { industry } });
      toast.success(
        `Found ${result.found} businesses — ${result.added} new, ${result.scanned} sites checked.`,
      );
      await refreshProspects();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Search failed");
    } finally {
      setBusy(null);
    }
  };

  const scanPendingSites = async () => {
    setBusy("scan");
    try {
      const { scanned } = await scanPendingFn();
      toast.success(`Checked ${scanned} more site(s).`);
      await refreshProspects();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Site check failed");
    } finally {
      setBusy(null);
    }
  };

  const draftProspectEmail = async (id: string) => {
    setBusy(`draft-${id}`);
    try {
      await draftOutreachFn({ data: { id } });
      toast.success("Draft ready for your review.");
      await refreshProspects();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not write the draft");
      await refreshProspects();
    } finally {
      setBusy(null);
    }
  };

  const saveProspectDraft = async (
    id: string,
    subject: string,
    body: string,
    contactEmail: string | null,
  ) => {
    setBusy(`save-${id}`);
    try {
      await saveProspectDraftFn({ data: { id, subject, body, contactEmail } });
      toast.success("Draft saved.");
      await refreshProspects();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not save the draft");
    } finally {
      setBusy(null);
    }
  };

  const sendProspectEmail = async (id: string) => {
    setBusy(`send-${id}`);
    try {
      const result = await sendOutreachFn({ data: { id } });
      if (result.ok) toast.success("Email sent.");
      else toast.error(`Not delivered: ${result.reason}`);
      await refreshProspects();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Send failed");
      await refreshProspects();
    } finally {
      setBusy(null);
    }
  };

  const updateProspect = async (
    id: string,
    patch: { status?: string; contactEmail?: string | null },
  ) => {
    try {
      await updateProspectFn({ data: { id, ...patch } as never });
      await refreshProspects();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not update prospect");
    }
  };

  const { data: portfolioData } = useQuery({
    queryKey: ["admin-portfolio"],
    queryFn: () => listPortfolio(),
    retry: false,
  });

  // Stats
  const activeProjectsCount = (ordersData?.orders ?? []).length;
  const unreadInquiriesCount = (inquiriesData?.inquiries ?? []).filter(
    (i) => i.status === "unread",
  ).length;
  const pendingBalanceTotal = (ordersData?.orders ?? [])
    .filter((o) => o.balance_due_cents > 0 && o.balance_status === "pending")
    .reduce((sum, o) => sum + o.balance_due_cents, 0);

  const refreshPipeline = () => queryClient.invalidateQueries({ queryKey: ["admin-pipeline"] });

  const changeLeadStage = async (leadId: string, stage: string) => {
    const res = await updateLeadStage({ data: { leadId, stage } });
    if (!res.success) throw new Error(res.error || "Update failed");
    await refreshPipeline();
  };

  const changeBookingStatus = async (bookingId: string, status: string) => {
    const res = await updateBookingStatus({ data: { bookingId, status } });
    if (!res.success) throw new Error(res.error || "Update failed");
    await refreshPipeline();
  };

  const closeFollowup = async (followupId: string, status: string) => {
    const res = await resolveFollowup({ data: { followupId, status } });
    if (!res.success) throw new Error(res.error || "Update failed");
    await refreshPipeline();
  };

  // Invoicing Action
  const invoiceBalance = async (orderId: string) => {
    setBusy(orderId);
    try {
      const result = await sendInvoice({ data: { orderId, environment } });
      if ("error" in result) throw new Error(result.error);
      toast.success("Balance invoice sent via Stripe");
      await queryClient.invalidateQueries({ queryKey: ["admin-orders", environment] });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not send invoice");
    } finally {
      setBusy(null);
    }
  };

  // Milestone Update Action
  const changeMilestone = async (briefId: string, newStatus: string) => {
    try {
      const res = await updateMilestone({ data: { briefId, projectStatus: newStatus } });
      if (!res.success) throw new Error(res.error || "Update failed");
      toast.success("Project milestone updated");
      await queryClient.invalidateQueries({ queryKey: ["admin-orders", environment] });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to update milestone");
    }
  };

  // Inquiry Status Action
  const toggleInquiryStatus = async (
    inquiryId: string,
    status: "unread" | "replied" | "archived",
  ) => {
    try {
      await updateInquiry({ data: { inquiryId, status } });
      toast.success(`Message marked as ${status}`);
      await queryClient.invalidateQueries({ queryKey: ["admin-inquiries"] });
    } catch (_err) {
      toast.error("Failed to update message status");
    }
  };

  // Portfolio Project Actions
  const handleOpenEditProject = (project: PortfolioProject) => {
    setEditingProject(project);
    setProjectForm({
      id: project.id,
      title: project.title,
      tagline: project.tagline,
      description: project.description,
      url: project.url,
      category: project.category,
      metric: project.metric || "",
      tags: project.tags.join(", "),
      isPublished: project.is_published ?? true,
    });
    setIsNewProjectModalOpen(true);
  };

  const handleOpenNewProject = () => {
    setEditingProject(null);
    setProjectForm({
      title: "",
      tagline: "",
      description: "",
      url: "https://",
      category: "Brand Identity",
      metric: "",
      tags: "UI/UX, Brand Identity, No-Code",
      isPublished: true,
    });
    setIsNewProjectModalOpen(true);
  };

  const handleSaveProject = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!projectForm.title || !projectForm.description) {
      toast.error("Title and description are required");
      return;
    }
    try {
      const tagsArray = projectForm.tags
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean);

      const res = await upsertPortfolio({
        data: {
          ...(projectForm.id ? { id: projectForm.id } : {}),
          title: projectForm.title,
          tagline: projectForm.tagline,
          description: projectForm.description,
          url: projectForm.url,
          category: projectForm.category,
          ...(projectForm.metric ? { metric: projectForm.metric } : {}),
          tags: tagsArray,
          isPublished: projectForm.isPublished,
        },
      });

      if (!res.success) throw new Error(res.error || "Save failed");
      toast.success(editingProject ? "Project updated" : "New project added to showcase");
      setIsNewProjectModalOpen(false);
      await queryClient.invalidateQueries({ queryKey: ["admin-portfolio"] });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not save project");
    }
  };

  const handleDeleteProject = async (projectId: string) => {
    try {
      await deletePortfolio({ data: { projectId } });
      toast.success("Project removed");
      await queryClient.invalidateQueries({ queryKey: ["admin-portfolio"] });
    } catch (_err) {
      toast.error("Failed to delete project");
    }
  };

  const submitProposal = async (mode: "draft" | "sent") => {
    if (!proposalForm.clientName || !proposalForm.clientEmail || !proposalForm.projectTitle) {
      toast.error("Client name, email, and project title are required");
      return;
    }
    const payload = {
      clientName: proposalForm.clientName,
      clientEmail: proposalForm.clientEmail,
      ...(proposalForm.clientCompany ? { clientCompany: proposalForm.clientCompany } : {}),
      projectTitle: proposalForm.projectTitle,
      scopeDeliverables: proposalForm.scopeDeliverables,
      timelineWeeks: proposalForm.timelineWeeks,
      totalPriceCents: Math.round(proposalForm.totalPriceDollars * 100),
      terms: proposalForm.terms,
    };

    try {
      if (editingProposalId) {
        const res = await updateProposal({ data: { id: editingProposalId, ...payload } });
        if (!res.success) throw new Error(res.error || "Failed to save proposal");
        if (mode === "sent") {
          const sent = await sendProposal({ data: { id: editingProposalId } });
          if (!sent.success) throw new Error(sent.error || "Failed to send proposal");
          toast.success(
            sent.emailed
              ? "Proposal sent to the client"
              : "Proposal published (email not delivered)",
          );
        } else {
          toast.success("Draft saved");
        }
      } else {
        const res = await createProposal({
          data: {
            ...(proposalForm.briefId ? { briefId: proposalForm.briefId } : {}),
            ...payload,
            status: mode,
          },
        });
        if (!res.success || !res.proposal)
          throw new Error(res.error || "Failed to create proposal");
        if (mode === "sent") {
          const sent = await sendProposal({ data: { id: res.proposal.id } });
          toast.success(
            sent.success && sent.emailed
              ? "Proposal sent to the client"
              : "Proposal published (email not delivered)",
          );
        } else {
          toast.success("Draft saved");
        }
      }

      setIsProposalModalOpen(false);
      setEditingProposalId(null);
      await queryClient.invalidateQueries({ queryKey: ["admin-proposals"] });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Error saving proposal");
    }
  };

  const handleSendProposalItem = async (id: string) => {
    try {
      const res = await sendProposal({ data: { id } });
      if (!res.success) throw new Error(res.error || "Send failed");
      toast.success(
        res.emailed ? "Proposal sent to the client" : "Proposal published (email not delivered)",
      );
      await queryClient.invalidateQueries({ queryKey: ["admin-proposals"] });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Error sending proposal");
    }
  };

  const handleEditProposal = (prop: ProjectProposal) => {
    setProposalForm({
      clientName: prop.client_name,
      clientEmail: prop.client_email,
      clientCompany: prop.client_company || "",
      projectTitle: prop.project_title,
      scopeDeliverables: prop.scope_deliverables,
      timelineWeeks: prop.timeline_weeks,
      totalPriceDollars: prop.total_price_cents / 100,
      terms: prop.terms,
    });
    setEditingProposalId(prop.id);
    setIsProposalModalOpen(true);
  };

  const handleDeleteProposalItem = async (id: string) => {
    try {
      const res = await deleteProposal({ data: { id } });
      if (!res.success) throw new Error(res.error || "Delete failed");
      toast.success("Proposal deleted");
      await queryClient.invalidateQueries({ queryKey: ["admin-proposals"] });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Error deleting proposal");
    }
  };

  const handleOpenProposalFromBrief = (brief: AdminBrief) => {
    setProposalForm({
      briefId: brief.id,
      clientName: brief.name,
      clientEmail: brief.email,
      clientCompany: brief.company || "",
      projectTitle: `${brief.project_type} — Custom Scope Agreement`,
      scopeDeliverables:
        brief.deliverables ||
        "• Visual Direction Sprint (Type, Color, UI Language)\n• Custom Screen Designs in Figma (Desktop & Mobile)\n• Interactive WebGL Animations & Build Handover\n• Stripe eCommerce Integration\n• 2 Comprehensive Revision Rounds",
      timelineWeeks: brief.timeline || "2–3 Weeks",
      totalPriceDollars: brief.budget && brief.budget.includes("10,000") ? 10000 : 5000,
      terms: DEFAULT_TERMS,
    });
    setSelectedBrief(null);
    setEditingProposalId(null);
    setIsProposalModalOpen(true);
  };

  return (
    <main className="admin-dashboard min-h-screen bg-background px-5 pb-20 pt-6 md:px-10 md:pt-10">
      <Toaster />
      <div className="mx-auto max-w-[1440px]">
        {/* Top Header */}
        <div className="admin-header border-b border-border pb-6">
          <div className="admin-brand flex items-center gap-4">
            <Logo variant="compact" size="md" href="/" />
            <div>
              <span className="font-mono text-[10px] font-semibold text-primary">STUDIO COMMAND HUB</span>
              <h1 className="font-display text-3xl font-bold leading-tight text-foreground sm:text-4xl">Dashboard</h1>
            </div>
          </div>
          <div className="admin-header-actions flex flex-wrap gap-2">
            <Link
              to="/briefs"
              className="admin-utility-link"
            >
              Project briefs
            </Link>
            <Link
              to="/account"
              className="admin-utility-link"
            >
              Client account
            </Link>
            <Link
              to="/"
              className="admin-utility-link"
            >
              Live site ↗
            </Link>
          </div>
        </div>

        {/* Quick KPI Stat Cards */}
        <div className="admin-overview mt-6 grid grid-cols-2 border-y border-border lg:grid-cols-4">
          <div className="admin-overview-item py-5 pr-4">
            <span className="font-mono text-[11px] text-foreground/70">
              Active Projects
            </span>
            <p className="mt-1 font-display text-4xl font-semibold leading-none text-foreground">{activeProjectsCount}</p>
            <span className="mt-2 block text-xs text-foreground/60">
              Commissions & Retainers
            </span>
          </div>

          <div className="admin-overview-item py-5 pr-4">
            <span className="font-mono text-[11px] text-foreground/70">
              New Inquiries
            </span>
            <p className="mt-1 font-display text-4xl font-semibold leading-none text-primary">{unreadInquiriesCount}</p>
            <span className="mt-2 block text-xs text-foreground/60">
              {unreadInquiriesCount === 0 ? "Inbox up to date" : "Unread client messages"}
            </span>
          </div>

          <div className="admin-overview-item py-5 pr-4">
            <span className="font-mono text-[11px] text-foreground/70">
              Pending Balances
            </span>
            <p className="mt-1 font-display text-4xl font-semibold leading-none text-foreground">
              {money(pendingBalanceTotal, "USD")}
            </p>
            <span className="mt-2 block text-xs text-foreground/60">
              Awaiting project completion
            </span>
          </div>

          <div className="admin-overview-item py-5 pr-4">
            <span className="font-mono text-[11px] text-foreground/70">
              Showcase Work
            </span>
            <p className="mt-1 font-display text-4xl font-semibold leading-none text-foreground">
              {(portfolioData?.projects ?? []).length}
            </p>
            <span className="mt-2 block text-xs text-foreground/60">
              Live portfolio items
            </span>
          </div>
        </div>

        {/* View Switcher Tabs */}
        <nav aria-label="Dashboard views" className="admin-tabs mt-8 flex gap-1 overflow-x-auto border-b border-border pb-0">
          {[
            { id: "SIGNAL", label: "SIGNAL HUB", icon: Radar },
            { id: "PROJECTS", label: "CURRENT PROJECTS", icon: Briefcase },
            {
              id: "PIPELINE",
              label: `LEAD PIPELINE (${(pipelineData?.leads ?? []).length})`,
              icon: Users,
            },
            {
              id: "SETUP",
              label: `NEW PURCHASE SETUP (${(onboardingData?.runs ?? []).filter((r) => r.status === "ready" || r.status === "failed").length})`,
              icon: Rocket,
            },
            { id: "COLDCALLS", label: "COLD CALLS", icon: Phone },
            {
              id: "AUTOPILOT",
              label: `FOLLOW-UP AUTOPILOT (${(autopilotData?.drafts ?? []).filter((d) => d.status === "draft").length})`,
              icon: Bot,
            },
            {
              id: "PROSPECTS",
              label: `PROSPECT FINDER (${(prospectsData?.prospects ?? []).filter((p) => p.pain_score >= 20 && p.status === "new").length})`,
              icon: Radar,
            },
            {
              id: "INQUIRIES",
              label: `CLIENT LEADS (${unreadInquiriesCount})`,
              icon: MessageSquare,
            },
            {
              id: "CHATS",
              label: `WEBSITE CHAT (${(chatsData?.conversations ?? []).filter((c) => c.unread_count > 0).length})`,
              icon: MessageSquare,
            },
            {
              id: "PROPOSALS",
              label: `PROPOSALS & CONTRACTS (${(proposalsData ?? []).length})`,
              icon: FileCheck,
            },
            { id: "PORTFOLIO", label: "PORTFOLIO MANAGER", icon: Eye },
            { id: "CLIENTPORTAL", label: "CLIENT PORTAL", icon: FolderKanban },
            { id: "OPPORTUNITY", label: "DEAL FINDER", icon: DollarSign },
            {
              id: "REDESIGN",
              label: `REDESIGN & PITCH (${(redesignData?.runs ?? []).length})`,
              icon: Rocket,
            },
            { id: "FINANCIALS", label: "FINANCIALS & STATS", icon: DollarSign },
          ].map((tab) => {
            const Icon = tab.icon;
            const isActive = currentView === tab.id;
            return (
              <Button
                key={tab.id}
                type="button"
                onClick={() => setCurrentView(tab.id as MainView)}
                aria-current={isActive ? "page" : undefined}
                variant="ghost"
                className={`admin-tab flex shrink-0 items-center gap-2 rounded-none border-b-2 px-4 py-3 font-sans text-sm font-medium transition-colors ${
                  isActive
                    ? "border-primary bg-primary/10 text-foreground"
                    : "border-transparent text-foreground/65 hover:bg-card hover:text-foreground"
                }`}
              >
                <Icon className="size-3.5" />
                {tab.label}
              </Button>
            );
          })}
        </nav>

        {ordersError && (
          <p className="mt-10 font-mono text-xs text-amber-300">
            You don’t have admin access on this account. Please sign in as an admin.
          </p>
        )}

        {/* Dynamic Views */}
        <div className="mt-8">
          {currentView === "COLDCALLS" && (
            <AdminColdCallsView
              calls={coldCallData?.calls ?? []}
              busy={busy}
              prefill={coldCallPrefill}
              onStart={startColdCall}
              onDelete={removeColdCall}
              date={date}
            />
          )}

          {currentView === "OPPORTUNITY" && (
            <AdminOpportunityView
              onCallLead={(c) => {
                setColdCallPrefill({
                  businessName: c.business,
                  phone: c.phone,
                  website: c.website,
                  talkingPoints: [c.offer, c.notes].filter(Boolean).join("\n\n").slice(0, 2000),
                });
                setCurrentView("COLDCALLS");
              }}
              onLeadSaved={() =>
                void queryClient.invalidateQueries({ queryKey: ["admin-pipeline"] })
              }
              onProposal={(r) => {
                const cents = Number(
                  (r.primary.price.match(/[\d,]+/)?.[0] ?? "").replace(/,/g, ""),
                );
                setProposalForm({
                  clientName: r.business,
                  clientEmail: r.contactEmail ?? "",
                  clientCompany: r.business,
                  projectTitle: `${r.primary.service} — ${r.business}`,
                  scopeDeliverables: [
                    `• ${r.primary.service}`,
                    ...r.upsells.map((u) => `• Optional: ${u.service} (${u.price})`),
                    "",
                    ...r.findings.map((f) => `Addresses: ${f}`),
                  ].join("\n"),
                  timelineWeeks: "2–3 Weeks",
                  totalPriceDollars: cents > 0 ? cents : 5000,
                  terms: DEFAULT_TERMS,
                });
                setEditingProposalId(null);
                setIsProposalModalOpen(true);
              }}
              onCall={(r) => {
                setColdCallPrefill({
                  businessName: r.business,
                  website: r.url,
                  talkingPoints: r.callScript,
                });
                setCurrentView("COLDCALLS");
              }}
            />
          )}

          {currentView === "REDESIGN" && (
            <AdminRedesignView
              runs={redesignData?.runs ?? []}
              busy={busy}
              onRun={startRedesign}
              onSetStatus={setRedesignStatus}
              onDelete={removeRedesignRun}
              onCall={(run) => {
                setColdCallPrefill({
                  businessName: run.host,
                  website: run.url,
                  talkingPoints:
                    [run.headline, run.outreach_body].filter(Boolean).join("\n\n") || null,
                  redesignRunId: run.id,
                });
                setCurrentView("COLDCALLS");
              }}
              onSaveOutreach={saveRedesignOutreach}
              onSend={sendRedesignPitch}
              onExportPdf={exportRedesignPdf}
              onOpenLeadPipeline={() => setCurrentView("PIPELINE")}
              date={date}
            />
          )}

          {currentView === "SIGNAL" && (
            <AdminSignalView
              orders={ordersData?.orders ?? []}
              inquiries={inquiriesData?.inquiries ?? []}
              leads={pipelineData?.leads ?? []}
              proposals={proposalsData ?? []}
              onboardingRuns={onboardingData?.runs ?? []}
              drafts={autopilotData?.drafts ?? []}
              unreadChats={
                (chatsData?.conversations ?? []).filter((c) => c.unread_count > 0).length
              }
              onNavigate={(view) => setCurrentView(view as MainView)}
              onRunAutopilot={runAutopilotScan}
              autopilotBusy={busy === "autopilot-run"}
            />
          )}

          {currentView === "PROJECTS" && (
            <AdminProjectsView
              orders={ordersData?.orders ?? []}
              filterTab={filterTab}
              setFilterTab={setFilterTab}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              onSendInvoice={invoiceBalance}
              onUpdateMilestone={changeMilestone}
              onViewBrief={(b) => setSelectedBrief(b)}
              onCreateProposalFromBrief={handleOpenProposalFromBrief}
              busy={busy}
              money={money}
              date={date}
            />
          )}

          {currentView === "SETUP" && (
            <AdminOnboardingView
              runs={onboardingData?.runs ?? []}
              busy={busy}
              onRunQueue={runOnboardingQueue}
              onApprove={approveOnboarding}
              onRetry={retryOnboarding}
              onDismiss={dismissOnboarding}
              date={date}
              money={money}
            />
          )}

          {currentView === "AUTOPILOT" && (
            <AdminAutopilotView
              state={autopilotData}
              busy={busy}
              onRun={runAutopilotScan}
              onTogglePause={toggleAutopilot}
              onApprove={approveDraft}
              onDismiss={dismissDraft}
              onRetry={retryDraft}
              onSave={saveDraft}
              date={date}
            />
          )}

          {currentView === "PROSPECTS" && (
            <AdminProspectsView
              prospects={prospectsData?.prospects ?? []}
              analytics={prospectAnalytics}
              onGenerateVariants={generateProspectVariants}
              onSelectVariant={chooseProspectVariant}
              onSyncCrm={syncProspectPipeline}

              busy={busy}
              onFind={findProspectsFor}

              onScanPending={scanPendingSites}
              onDraft={draftProspectEmail}
              onSaveDraft={saveProspectDraft}
              onSend={sendProspectEmail}
              onUpdate={updateProspect}
              date={date}
            />
          )}

          {currentView === "CLIENTPORTAL" && <AdminPortalView />}

          {currentView === "PIPELINE" && (
            <AdminPipelineView
              leads={pipelineData?.leads ?? []}
              onUpdateStage={changeLeadStage}
              onUpdateBooking={changeBookingStatus}
              onResolveFollowup={closeFollowup}
              date={date}
            />
          )}

          {currentView === "INQUIRIES" && (
            <AdminInquiriesView
              inquiries={inquiriesData?.inquiries ?? []}
              briefs={ordersData?.briefs ?? []}
              onUpdateInquiryStatus={toggleInquiryStatus}
              onViewBrief={(b) => setSelectedBrief(b)}
              date={date}
            />
          )}

          {currentView === "CHATS" && (
            <AdminChatsView
              conversations={chatsData?.conversations ?? []}
              onUpdateStatus={setChatStatus}
              date={date}
            />
          )}

          {currentView === "PROPOSALS" && (
            <AdminProposalsView
              proposals={proposalsData ?? []}
              onCreateProposal={() => {
                setProposalForm({
                  clientName: "",
                  clientEmail: "",
                  clientCompany: "",
                  projectTitle: "Custom Design + Build Scope",
                  scopeDeliverables:
                    "• Visual Direction Sprint (Type, Color, UI Language)\n• Full Mobile & Desktop Screen Designs in Figma\n• Complete Production Build & Interactive Animations\n• Stripe eCommerce / Payment Integration\n• SEO Meta Architecture & Performance Optimization\n• 2 Comprehensive Revision Rounds",
                  timelineWeeks: "2–3 Weeks",
                  totalPriceDollars: 5000,
                  terms: DEFAULT_TERMS,
                });
                setEditingProposalId(null);
                setIsProposalModalOpen(true);
              }}
              onEditProposal={handleEditProposal}
              onSendProposal={handleSendProposalItem}
              onDeleteProposal={handleDeleteProposalItem}
              money={money}
              date={date}
            />
          )}

          {currentView === "PORTFOLIO" && (
            <AdminPortfolioCMS
              projects={portfolioData?.projects ?? []}
              onOpenNewModal={handleOpenNewProject}
              onEditProject={handleOpenEditProject}
              onDeleteProject={handleDeleteProject}
            />
          )}

          {currentView === "FINANCIALS" && (
            <AdminFinancialsView orders={ordersData?.orders ?? []} money={money} date={date} />
          )}
        </div>

        {/* MODAL 1: BRIEF DETAIL VIEWER */}
        {selectedBrief && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm">
            <div className="relative max-h-[85vh] w-full max-w-2xl overflow-y-auto border border-white/15 bg-[#030014] p-6 shadow-2xl">
              <div className="flex items-start justify-between border-b border-white/10 pb-4">
                <div>
                  <span className="font-mono text-[10px] tracking-widest text-[#FF3333]">
                    PROJECT BRIEF INTAKE
                  </span>
                  <h2 className="mt-1 font-display text-2xl uppercase text-white">
                    {selectedBrief.name}
                  </h2>
                  <p className="font-mono text-xs text-white/50">{selectedBrief.email}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedBrief(null)}
                  className="text-white/40 hover:text-white"
                >
                  <X className="size-5" />
                </button>
              </div>

              <div className="mt-6 space-y-4 font-mono text-xs text-white/80">
                <div>
                  <span className="text-[#FF3333]">PROJECT TYPE:</span> {selectedBrief.project_type}
                </div>
                <div>
                  <span className="text-[#FF3333]">GOALS:</span> {selectedBrief.goals}
                </div>
                {selectedBrief.audience && (
                  <div>
                    <span className="text-[#FF3333]">TARGET AUDIENCE:</span>{" "}
                    {selectedBrief.audience}
                  </div>
                )}
                {selectedBrief.deliverables && (
                  <div>
                    <span className="text-[#FF3333]">DELIVERABLES:</span>{" "}
                    {selectedBrief.deliverables}
                  </div>
                )}
                {selectedBrief.budget && (
                  <div>
                    <span className="text-[#FF3333]">BUDGET RANGE:</span> {selectedBrief.budget}
                  </div>
                )}
                {selectedBrief.timeline && (
                  <div>
                    <span className="text-[#FF3333]">TIMELINE:</span> {selectedBrief.timeline}
                  </div>
                )}
              </div>

              <div className="mt-8 flex justify-end gap-3 border-t border-white/10 pt-4">
                <button
                  onClick={() => handleOpenProposalFromBrief(selectedBrief)}
                  className="bg-[#FF3333] px-4 py-2 font-mono text-xs font-bold text-black hover:opacity-90"
                >
                  GENERATE PROPOSAL FROM BRIEF →
                </button>
                <button
                  onClick={() => setSelectedBrief(null)}
                  className="border border-white/15 px-4 py-2 font-mono text-xs text-white/60 hover:text-white"
                >
                  CLOSE
                </button>
              </div>
            </div>
          </div>
        )}

        {/* MODAL 2: ADD / EDIT PORTFOLIO PROJECT */}
        {isNewProjectModalOpen && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm">
            <div className="relative max-h-[85vh] w-full max-w-lg overflow-y-auto border border-white/15 bg-[#030014] p-6 shadow-2xl">
              <div className="flex items-start justify-between border-b border-white/10 pb-4">
                <div>
                  <span className="font-mono text-[10px] tracking-widest text-[#FF3333]">
                    PORTFOLIO CMS
                  </span>
                  <h2 className="mt-1 font-display text-2xl uppercase text-white">
                    {editingProject ? "Edit Showcase Project" : "Add Showcase Project"}
                  </h2>
                </div>
                <button
                  type="button"
                  onClick={() => setIsNewProjectModalOpen(false)}
                  className="text-white/40 hover:text-white"
                >
                  <X className="size-5" />
                </button>
              </div>

              <form onSubmit={handleSaveProject} className="mt-6 space-y-4 font-mono text-xs">
                <div>
                  <label className="block text-[10px] text-white/40">PROJECT TITLE</label>
                  <input
                    className="mt-1 w-full border border-white/15 bg-white/[0.02] p-2 text-white focus:border-[#FF3333] focus:outline-none"
                    value={projectForm.title}
                    onChange={(e) => setProjectForm({ ...projectForm, title: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="block text-[10px] text-white/40">TAGLINE</label>
                  <input
                    className="mt-1 w-full border border-white/15 bg-white/[0.02] p-2 text-white focus:border-[#FF3333] focus:outline-none"
                    value={projectForm.tagline}
                    onChange={(e) => setProjectForm({ ...projectForm, tagline: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-[10px] text-white/40">CATEGORY</label>
                  <select
                    className="mt-1 w-full border border-white/15 bg-[#030014] p-2 text-white focus:border-[#FF3333] focus:outline-none"
                    value={projectForm.category}
                    onChange={(e) =>
                      setProjectForm({
                        ...projectForm,
                        category: e.target.value as "Brand Identity" | "UI/UX" | "No-Code",
                      })
                    }
                  >
                    <option value="Brand Identity">Brand Identity</option>
                    <option value="UI/UX">UI/UX</option>
                    <option value="No-Code">No-Code</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] text-white/40">LIVE DEMO URL</label>
                  <input
                    className="mt-1 w-full border border-white/15 bg-white/[0.02] p-2 text-white focus:border-[#FF3333] focus:outline-none"
                    value={projectForm.url}
                    onChange={(e) => setProjectForm({ ...projectForm, url: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-[10px] text-white/40">DESCRIPTION</label>
                  <textarea
                    rows={3}
                    className="mt-1 w-full border border-white/15 bg-white/[0.02] p-2 text-white focus:border-[#FF3333] focus:outline-none"
                    value={projectForm.description}
                    onChange={(e) =>
                      setProjectForm({ ...projectForm, description: e.target.value })
                    }
                    required
                  />
                </div>
                <div>
                  <label className="block text-[10px] text-white/40">
                    FEATURE METRIC (OPTIONAL)
                  </label>
                  <input
                    placeholder="e.g. 40% faster checkout"
                    className="mt-1 w-full border border-white/15 bg-white/[0.02] p-2 text-white focus:border-[#FF3333] focus:outline-none"
                    value={projectForm.metric}
                    onChange={(e) => setProjectForm({ ...projectForm, metric: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-[10px] text-white/40">TAGS (COMMA SEPARATED)</label>
                  <input
                    placeholder="UI/UX, Brand Identity, No-Code Build"
                    className="mt-1 w-full border border-white/15 bg-white/[0.02] p-2 text-white focus:border-[#FF3333] focus:outline-none"
                    value={projectForm.tags}
                    onChange={(e) => setProjectForm({ ...projectForm, tags: e.target.value })}
                  />
                </div>

                <div className="mt-6 flex justify-end gap-3 border-t border-white/10 pt-4">
                  <button
                    type="submit"
                    className="bg-[#FF3333] px-4 py-2 font-mono text-xs font-bold text-black hover:opacity-90"
                  >
                    SAVE PROJECT →
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsNewProjectModalOpen(false)}
                    className="border border-white/15 px-4 py-2 font-mono text-xs text-white/60 hover:text-white"
                  >
                    CANCEL
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* MODAL 3: CREATE PROPOSAL */}
        {isProposalModalOpen && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm">
            <div className="relative max-h-[85vh] w-full max-w-lg overflow-y-auto border border-white/15 bg-[#030014] p-6 shadow-2xl">
              <div className="flex items-start justify-between border-b border-white/10 pb-4">
                <div>
                  <span className="font-mono text-[10px] tracking-widest text-[#FF3333]">
                    PROPOSAL BUILDER
                  </span>
                  <h2 className="mt-1 font-display text-2xl uppercase text-white">
                    {editingProposalId ? "Edit Scope Agreement" : "Create Scope Agreement"}
                  </h2>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setIsProposalModalOpen(false);
                    setEditingProposalId(null);
                  }}
                  className="text-white/40 hover:text-white"
                >
                  <X className="size-5" />
                </button>
              </div>

              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  void submitProposal("sent");
                }}
                className="mt-6 space-y-4 font-mono text-xs"
              >
                <div>
                  <label className="block text-[10px] text-white/40">CLIENT NAME</label>
                  <input
                    className="mt-1 w-full border border-white/15 bg-white/[0.02] p-2 text-white focus:border-[#FF3333] focus:outline-none"
                    value={proposalForm.clientName}
                    onChange={(e) =>
                      setProposalForm({ ...proposalForm, clientName: e.target.value })
                    }
                    required
                  />
                </div>
                <div>
                  <label className="block text-[10px] text-white/40">CLIENT EMAIL</label>
                  <input
                    type="email"
                    className="mt-1 w-full border border-white/15 bg-white/[0.02] p-2 text-white focus:border-[#FF3333] focus:outline-none"
                    value={proposalForm.clientEmail}
                    onChange={(e) =>
                      setProposalForm({ ...proposalForm, clientEmail: e.target.value })
                    }
                    required
                  />
                </div>
                <div>
                  <label className="block text-[10px] text-white/40">COMPANY NAME (OPTIONAL)</label>
                  <input
                    className="mt-1 w-full border border-white/15 bg-white/[0.02] p-2 text-white focus:border-[#FF3333] focus:outline-none"
                    value={proposalForm.clientCompany}
                    onChange={(e) =>
                      setProposalForm({ ...proposalForm, clientCompany: e.target.value })
                    }
                  />
                </div>
                <div>
                  <label className="block text-[10px] text-white/40">PROJECT TITLE</label>
                  <input
                    className="mt-1 w-full border border-white/15 bg-white/[0.02] p-2 text-white focus:border-[#FF3333] focus:outline-none"
                    value={proposalForm.projectTitle}
                    onChange={(e) =>
                      setProposalForm({ ...proposalForm, projectTitle: e.target.value })
                    }
                    required
                  />
                </div>
                <div>
                  <label className="block text-[10px] text-white/40">SCOPE DELIVERABLES</label>
                  <textarea
                    rows={4}
                    className="mt-1 w-full border border-white/15 bg-white/[0.02] p-2 text-white focus:border-[#FF3333] focus:outline-none"
                    value={proposalForm.scopeDeliverables}
                    onChange={(e) =>
                      setProposalForm({ ...proposalForm, scopeDeliverables: e.target.value })
                    }
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[10px] text-white/40">TIMELINE</label>
                    <input
                      className="mt-1 w-full border border-white/15 bg-white/[0.02] p-2 text-white focus:border-[#FF3333] focus:outline-none"
                      value={proposalForm.timelineWeeks}
                      onChange={(e) =>
                        setProposalForm({ ...proposalForm, timelineWeeks: e.target.value })
                      }
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] text-white/40">TOTAL PRICE ($ USD)</label>
                    <input
                      type="number"
                      className="mt-1 w-full border border-white/15 bg-white/[0.02] p-2 text-white focus:border-[#FF3333] focus:outline-none"
                      value={proposalForm.totalPriceDollars}
                      onChange={(e) =>
                        setProposalForm({
                          ...proposalForm,
                          totalPriceDollars: Number(e.target.value),
                        })
                      }
                      required
                    />
                  </div>
                </div>

                <div className="mt-6 flex justify-end gap-3 border-t border-white/10 pt-4">
                  <button
                    type="button"
                    onClick={() => void submitProposal("draft")}
                    className="border border-white/20 px-4 py-2 font-mono text-xs text-white hover:border-[#FF3333]"
                  >
                    SAVE DRAFT
                  </button>
                  <button
                    type="submit"
                    className="bg-[#FF3333] px-4 py-2 font-mono text-xs font-bold text-black hover:opacity-90"
                  >
                    SEND TO CLIENT →
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setIsProposalModalOpen(false);
                      setEditingProposalId(null);
                    }}
                    className="border border-white/15 px-4 py-2 font-mono text-xs text-white/60 hover:text-white"
                  >
                    CANCEL
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
